import React, { useState, useEffect } from "react";
import { addService, updateService, getAllServices, addPlanToService, updatePlanOfService } from "./servicesApi";
import { useTranslation } from "react-i18next"; // Import useTranslation
import { useNavigate, useLocation } from "react-router-dom"; // Import navigation hooks
import ServicePlanInformation from "./ServicePlanInformation"; // Assuming ServicePlanInformation.js is in the same directory
import Swal from "sweetalert2"; // Import Swal for alerts
import "../styles/Responsive.css"; // Import responsive styles


const AddNewService = ({ onClose, onServiceSaved, service, startWithPlanTab = false, planOnlyMode = false, editPlanMode = false, }) => {
  console.log("AddNewService - editPlanMode:", editPlanMode);
  console.log("AddNewService - service:", service);
  // Requirement: only one allowed service name in UI.
  const FIXED_SERVICE_NAME = "AI Based CHAT & VOICE BOT Prepaid Subscription";
  const [description, setDescription] = useState("");
  const [keyfeature, setKeyfeature] = useState("");
  const [topbenefits, setTopbenefits] = useState("");
  const [serviceName, setServiceName] = useState(FIXED_SERVICE_NAME);
  const [serviceCategory, setServiceCategory] = useState("SMR + PSF");
  const [categories, setCategories] = useState(["SMR + PSF"]);
  const [error, setError] = useState("");
  const [showServicePlans, setShowServicePlans] = useState(false);
  const [serviceDetails, setServiceDetails] = useState(null);
  const [serviceDetailsWithPlan, setServiceDetailsWithPlan] = useState(null);
  const [allServicesForValidation, setAllServicesForValidation] = useState([]); // Store all services for duplicate check

  const { t } = useTranslation(); // Initialize useTranslation
  const navigate = useNavigate();
  const location = useLocation();

  // Update URL based on which component is displayed
  // This ensures URL reflects the current view: AddNewService form -> /AdminHome/addService, ServicePlanInformation -> /AdminHome/addPlan
  useEffect(() => {
    // Determine the correct URL based on current state
    const correctPath = (planOnlyMode || showServicePlans) ? "/AdminHome/addPlan" : "/AdminHome/addService";

    // Only update if URL doesn't match the expected path
    if (location.pathname !== correctPath) {
      // Use replace to avoid adding to history and prevent back button issues
      navigate(correctPath, { replace: true });
    }
  }, [showServicePlans, planOnlyMode, navigate, location.pathname]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await getAllServices();
        setAllServicesForValidation(data); // Store for duplicate service name check
        // Category options: SMR + PSF (plan shows 4 fields) and DTXC (plan shows Total Calling Minutes / Total Messages)
        setCategories(["SMR + PSF", "DTXC"]);
      } catch (err) {
        console.error("Failed to fetch categories:", err);
        setCategories(["SMR + PSF", "DTXC"]);
      }
    };
    fetchCategories();
  }, []);

  // Prefill when editing
  useEffect(() => {
    if (service) {
      console.log("Raw service from backend:", service);

      // Dropdown is fixed to a single option, so always show the fixed value.
      setServiceName(FIXED_SERVICE_NAME);
      setServiceCategory(service.serviceInfo?.SERVICE_CATEGORY || "");
      setDescription(service.serviceInfo?.SERVICE_DESCRIPTION || "");
      setKeyfeature(service.serviceInfo?.SERVICE_FEATURES || "");
      setTopbenefits(service.serviceInfo?.SERVICE_BENEFITS || "");

      const plans = Array.isArray(service.planInfo)
        ? service.planInfo
        : service.planInfo
          ? [service.planInfo]
          : [];
      const details = {
        serviceName: service.serviceInfo?.SERVICE_NAME || "",
        serviceDescription: service.serviceInfo?.SERVICE_DESCRIPTION || "",
        serviceFeatures: service.serviceInfo?.SERVICE_FEATURES || "",
        serviceBenefits: service.serviceInfo?.SERVICE_BENEFITS || "",
        serviceReleaseDate:
          service.serviceInfo?.SERVICE_RELEASE_DATE ||
          new Date().toISOString().split("T")[0],
        serviceCategory: service.serviceInfo?.SERVICE_CATEGORY || "",
        serviceId: service.serviceInfo?.SERVICE_ID || "",
        planInfo: plans,
      };

      setServiceDetailsWithPlan(details);
      console.log("Service object with plans:", JSON.stringify(details, null, 2));

      if (startWithPlanTab || planOnlyMode) {
        setShowServicePlans(true);
      }
    }
  }, [service, startWithPlanTab, planOnlyMode]);





  const handleServiceNameChange = (e) => {
    const value = e.target.value;
    setServiceName(value);
    const newErrors = { ...error };

    // Since dropdown is fixed, only validate required.
    if (!value || value.trim() === "") {
      newErrors.serviceName = t("SERVICE_NAME_REQUIRED");
    } else {
      delete newErrors.serviceName;
    }
    setError(newErrors);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    let hasError = false;
    const newErrors = {};

    if (!serviceName || serviceName.trim() === "") {
      newErrors.serviceName = t("SERVICE_NAME_REQUIRED");
      hasError = true;
    } else if (serviceName !== FIXED_SERVICE_NAME) {
      // UI should never allow this; keeps validation consistent.
      newErrors.serviceName = "Invalid service name.";
      hasError = true;
    }

    if (!serviceCategory) {
      newErrors.serviceCategory = t("SERVICE_CATEGORY_REQUIRED");
      hasError = true;
    }

    if (!description.trim()) {
      newErrors.description = t("DETAILED_DESCRIPTION_REQUIRED");
      hasError = true;
    }

    if (!keyfeature.trim()) {
      newErrors.keyfeature = t("KEY_FEATURES_REQUIRED");
      hasError = true;
    }

    if (!topbenefits.trim()) {
      newErrors.topbenefits = t("TOP_BENEFITS_REQUIRED");
      hasError = true;
    }

    setError(newErrors);

    if (!hasError) {
      const details = {
        serviceName,
        serviceDescription: description,
        serviceFeatures: keyfeature,
        serviceBenefits: topbenefits,
        serviceReleaseDate: new Date().toISOString(),
        serviceCategory,
      };

      // If editing an existing service, call updateService directly
      if (service && service.serviceInfo?.SERVICE_ID) {
        handleUpdateServiceOnly(details);
      } else {
        // If creating a new service, go to plan page (existing behavior)
        setServiceDetails(details);
        setShowServicePlans(true);
      }
    }
  };

  const handleCancel = () => {
    // Navigate back to services page when canceling from service details
    navigate("/AdminHome/services", { replace: true });
    onClose();
  };

  const handleCancelPlans = () => {
    // When canceling from plan page, go back to service details page
    setShowServicePlans(false);
    // URL will be updated by useEffect when showServicePlans becomes false
  };
  const [showCategories, setShowCategories] = useState(false);

  const handleDraftService = async (planData) => {
    try {
      const isEditingExisting = !!service?.serviceInfo?.SERVICE_ID;
      const serviceId = service?.serviceInfo?.SERVICE_ID;

      // Duplicate service name is allowed (requirement). No pre-validation needed here.

      const payload = {
        serviceName: serviceDetails.serviceName,
        serviceDescription: serviceDetails.serviceDescription,
        serviceFeatures: serviceDetails.serviceFeatures,
        serviceBenefits: serviceDetails.serviceBenefits,
        serviceReleaseDate:
          serviceDetails.serviceReleaseDate ||
          new Date().toISOString().split("T")[0],
        serviceCategory: serviceDetails.serviceCategory,
        planInfo: [
          {
            planName: planData.planName,
            planStatus: "0",
            planTotalCallMinutes: planData.planTotalCallMinutes?.toString(),
            planTotalMessages: planData.planTotalMessages?.toString(),
            planProductPrice: planData.productPrice?.toString(),
            planPsfCallMinutes: planData.psfCallingMinutes?.toString(),
            planPsfMessages: planData.psfTotalMessages?.toString(),
            planSmrCallMinutes: planData.smrCallingMinutes?.toString(),
            planSmrMessages: planData.smrTotalMessages?.toString(),
          },
        ],
      };

      if (service) {
        await updateService(service.serviceInfo?.SERVICE_ID, payload);
        console.log("Draft Service Updated:", payload);
      } else {
        await addService(payload);
        console.log("Draft Service Created:", payload);
      }

      if (onServiceSaved) await onServiceSaved();
      onClose();
    } catch (error) {
      console.error("Error drafting service:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to save service draft. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };



  const handleAddPlanOnly = async (planData) => {
    try {
      if (!serviceDetails?.serviceId && !service?.serviceInfo?.SERVICE_ID) {
        console.error("No service selected for adding plan");
        return;
      }

      const serviceId = serviceDetails?.serviceId || service?.serviceInfo?.SERVICE_ID;

      // Requirement: service name can be duplicate,
      // but (service name + plan name) combination must be unique.
      try {
        const allServices = await getAllServices("all");
        const currentServiceName =
          serviceDetails?.serviceName ||
          service?.serviceInfo?.SERVICE_NAME ||
          FIXED_SERVICE_NAME;

        const inputServiceName = String(currentServiceName).toLowerCase().trim();
        const inputPlanName = String(planData.planName || "").toLowerCase().trim();

        const duplicatePlan = (allServices || []).some((s) => {
          const sName = String(s.serviceInfo?.SERVICE_NAME || "").toLowerCase().trim();
          if (!sName || sName !== inputServiceName) return false;
          const existingPlans = Array.isArray(s.planInfo)
            ? s.planInfo
            : s.planInfo
              ? [s.planInfo]
              : [];
          return existingPlans.some((p) => {
            const pName = String(p.PLAN_NAME || p.planName || "").toLowerCase().trim();
            return pName && pName === inputPlanName;
          });
        });

        if (duplicatePlan) {
          Swal.fire({
            title: "Duplicate Plan Name",
            text: `Plan name "${planData.planName}" already exists for "${currentServiceName}". Please choose a different plan name.`,
            icon: "error",
            confirmButtonText: "OK",
          });
          return; // Stop execution - don't make API call
        }
      } catch (validationError) {
        console.error("Error validating plan name:", validationError);
        // Continue with API call if validation fails (don't block user)
      }

      //Determine current service status
      const currentStatus = service?.serviceStatus || "0"; // "1" = ACTIVE, "0" = INACTIVE

      // Set planStatus conditionally
      const planStatus = currentStatus === "1"
        ? "1" // keep ACTIVE if currently active
        : "0"; // keep INACTIVE if currently inactive

      const payload = {
        planName: planData.planName,
        planStatus,
        planTotalCallMinutes: planData.planTotalCallMinutes?.toString(),
        planTotalMessages: planData.planTotalMessages?.toString(),
        planProductPrice: planData.productPrice?.toString(),
        planPsfCallMinutes: planData.psfCallingMinutes?.toString(),
        planPsfMessages: planData.psfTotalMessages?.toString(),
        planSmrCallMinutes: planData.smrCallingMinutes?.toString(),
        planSmrMessages: planData.smrTotalMessages?.toString(),
        numberOfUnitsSold: planData.numberOfUnitsSold || "0",
      };

      const response = await addPlanToService(serviceId, payload);
      console.log("Plan added successfully:", response);

      // Show success popup after successful API call
      await Swal.fire({
        title: "Plan Added Successfully!",
        text: "The plan has been added to the service.",
        icon: "success",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

      if (onServiceSaved) await onServiceSaved(); // refresh parent list
      onClose(); // close modal
    } catch (error) {
      console.error("Error adding plan:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to add plan. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleUpdateServiceOnly = async (serviceDetails) => {
    try {
      if (!service?.serviceInfo?.SERVICE_ID) {
        console.error("No service ID available for updating service");
        return;
      }

      const serviceId = service.serviceInfo.SERVICE_ID;
      // Duplicate service names are allowed (requirement). No validation here.

      const payload = {
        serviceName: serviceDetails.serviceName,
        serviceDescription: serviceDetails.serviceDescription,
        serviceFeatures: serviceDetails.serviceFeatures,
        serviceBenefits: serviceDetails.serviceBenefits,
        serviceReleaseDate: serviceDetails.serviceReleaseDate,
        serviceCategory: serviceDetails.serviceCategory,
        // Don't include planInfo when updating service only
      };

      const response = await updateService(serviceId, payload);
      console.log("Service updated successfully:", response);

      // Show success alert
      await Swal.fire({
        title: "Service Updated Successfully!",
        text: "The service details have been updated.",
        icon: "success",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

      if (onServiceSaved) await onServiceSaved(); // refresh parent list
      onClose(); // close modal
    } catch (error) {
      console.error("Error updating service:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to update service. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleUpdatePlan = async (planData) => {
    try {
      if (!service?.serviceInfo?.SERVICE_ID || !service?.planInfo?.[0]?.PLAN_ID) {
        console.error("No service ID or plan ID available for updating plan");
        return;
      }

      const serviceId = service.serviceInfo.SERVICE_ID;
      const planId = service.planInfo[0].PLAN_ID;

      // Requirement: (service name + plan name) must be unique.
      // When updating, exclude the currently edited plan id.
      try {
        const allServices = await getAllServices("all");
        const currentServiceName = service?.serviceInfo?.SERVICE_NAME || FIXED_SERVICE_NAME;
        const inputServiceName = String(currentServiceName).toLowerCase().trim();
        const inputPlanName = String(planData.planName || "").toLowerCase().trim();
        const currentPlanId = String(planId || "").trim();

        const duplicatePlan = (allServices || []).some((s) => {
          const sName = String(s.serviceInfo?.SERVICE_NAME || "").toLowerCase().trim();
          if (!sName || sName !== inputServiceName) return false;

          const existingPlans = Array.isArray(s.planInfo)
            ? s.planInfo
            : s.planInfo
              ? [s.planInfo]
              : [];
          return existingPlans.some((p) => {
            const pName = String(p.PLAN_NAME || p.planName || "").toLowerCase().trim();
            if (!pName || pName !== inputPlanName) return false;

            const pId = String(p.PLAN_ID ?? p.planId ?? "").trim();
            if (currentPlanId && pId === currentPlanId) return false;
            return true;
          });
        });

        if (duplicatePlan) {
          Swal.fire({
            title: "Duplicate Plan Name",
            text: `Plan name "${planData.planName}" already exists for "${currentServiceName}". Please choose a different plan name.`,
            icon: "error",
            confirmButtonText: "OK",
          });
          return; // Stop execution - don't make API call
        }
      } catch (validationError) {
        console.error("Error validating plan name:", validationError);
        // Continue with API call if validation fails (don't block user)
      }

      const payload = {
        planName: planData.planName,
        planTotalCallMinutes: planData.planTotalCallMinutes?.toString(),
        planTotalMessages: planData.planTotalMessages?.toString(),
        planProductPrice: planData.productPrice?.toString(),
        planPsfCallMinutes: planData.psfCallingMinutes?.toString(),
        planPsfMessages: planData.psfTotalMessages?.toString(),
        planSmrCallMinutes: planData.smrCallingMinutes?.toString(),
        planSmrMessages: planData.smrTotalMessages?.toString(),
        planStatus: service.planInfo[0].PLAN_STATUS || "1",
      };

      console.log("Updating plan with payload:", payload);
      console.log("Plan data received:", planData);

      const response = await updatePlanOfService(serviceId, planId, payload);
      console.log("Plan updated successfully:", response);

      // Show success popup after successful API call
      await Swal.fire({
        title: "Plan Updated Successfully!",
        text: "The plan details have been updated.",
        icon: "success",
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true,
      });

      if (onServiceSaved) {
        console.log("Calling onServiceSaved callback...");
        await onServiceSaved(); // refresh parent list
        console.log("onServiceSaved callback completed");
      }
      onClose(); // close modal
    } catch (error) {
      console.error("Error updating plan:", error);
      Swal.fire({
        title: "Error",
        text: error.response?.data?.message || "Failed to update plan. Please try again.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };


  // const handlePublishService = async (planData) => {
  //   try {
  //     const payload = {
  //       serviceName: serviceDetails.serviceName,
  //       serviceDescription: serviceDetails.serviceDescription,
  //       serviceFeatures: serviceDetails.serviceFeatures,
  //       serviceBenefits: serviceDetails.serviceBenefits,
  //       serviceReleaseDate:
  //         serviceDetails.serviceReleaseDate || new Date().toISOString().split("T")[0],
  //       serviceCategory: serviceDetails.serviceCategory,
  //       planInfo: [
  //         {
  //           planName: planData.planName,
  //           planStatus: "1", // Published
  //           planTotalCallMinutes: planData.totalCallingMinutes?.toString(),
  //           planTotalMessages: planData.totalMessages?.toString(),
  //           planProductPrice: planData.productPrice?.toString(),
  //         },
  //       ],
  //     };

  //     await addService(payload); // 👈 Always use addService

  //     console.log("Published Service Created:", payload);
  //     if (onServiceSaved) await onServiceSaved();
  //     onClose();
  //   } catch (error) {
  //     console.error("Error publishing service:", error);
  //   }
  // };


  const handlePublishService = async (planData) => {
    console.log("-----Plan Data------", planData);

    try {

      if (!planData || typeof planData !== "object") {
        Swal.fire({ title: "Error", text: "Invalid plan data. Please fill all required fields.", icon: "error", confirmButtonText: "OK" });
        return;
      }
  
      // Use serviceDetails if set (after Continue), else build from current form state so we never throw on null
      const effectiveDetails = serviceDetails || (showServicePlans && !planOnlyMode ? {
        serviceName,
        serviceDescription: description,
        serviceFeatures: keyfeature,
        serviceBenefits: topbenefits,
        serviceCategory,
        serviceReleaseDate: new Date().toISOString().split("T")[0],
      } : null);
  
      if (!effectiveDetails) {
        Swal.fire({
          title: "Error",
          text: "Please complete Service Details first (use Back and fill the form, then Continue).",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }
  
      // Determine current service status (before any checks that use it)
      const isEditingExisting = !!service?.serviceInfo?.SERVICE_ID;
      const currentStatus = service?.serviceStatus || "0"; // "1" = ACTIVE, "0" = INACTIVE
      const serviceId = service?.serviceInfo?.SERVICE_ID;

      if (!isEditingExisting && (!effectiveDetails.serviceName || !String(effectiveDetails.serviceName).trim())) {
        Swal.fire({
          title: "Error",
          text: "Please complete Service Details first (Service Name is required). Use Back and fill the form, then Continue.",
          icon: "error",
          confirmButtonText: "OK",
        });
        return;
      }

      // Requirement: service name can be duplicate,
      // but (service name + plan name) combination must be unique.
      try {
        const allServices = await getAllServices("all");
        const inputServiceName = String(effectiveDetails.serviceName || "").toLowerCase().trim();
        const inputPlanName = String(planData.planName || "").toLowerCase().trim();
        const currentPlanId = isEditingExisting ? String(service?.planInfo?.[0]?.PLAN_ID || "").trim() : "";

        const duplicatePlan = (allServices || []).some((s) => {
          const sName = String(s.serviceInfo?.SERVICE_NAME || "").toLowerCase().trim();
          if (!sName || sName !== inputServiceName) return false;

          const existingPlans = Array.isArray(s.planInfo)
            ? s.planInfo
            : s.planInfo
              ? [s.planInfo]
              : [];
          return existingPlans.some((p) => {
            const pName = String(p.PLAN_NAME || p.planName || "").toLowerCase().trim();
            if (!pName || pName !== inputPlanName) return false;

            const pId = String(p.PLAN_ID ?? p.planId ?? "").trim();
            if (currentPlanId && pId === currentPlanId) return false;
            return true;
          });
        });

        if (duplicatePlan) {
          Swal.fire({
            title: "Duplicate Plan Name",
            text: `Plan name "${planData.planName}" already exists for "${effectiveDetails.serviceName}". Please choose a different plan name.`,
            icon: "error",
            confirmButtonText: "OK",
          });
          return;
        }
      } catch (validationError) {
        console.error("Error validating plan name:", validationError);
      }

      // Set planStatus conditionally
      const planStatus = currentStatus === "1"
        ? "1" // keep ACTIVE if currently active
        : "0"; // keep INACTIVE if currently inactive

      const payload = {
        serviceName: effectiveDetails.serviceName,
      serviceDescription: effectiveDetails.serviceDescription,
      serviceFeatures: effectiveDetails.serviceFeatures,
      serviceBenefits: effectiveDetails.serviceBenefits,
        serviceReleaseDate:
        effectiveDetails.serviceReleaseDate ||
          new Date().toISOString().split("T")[0],
        serviceCategory: effectiveDetails.serviceCategory,
        planInfo: [
          {
            planName: planData.planName,
            planStatus,
            planTotalCallMinutes: (planData.planTotalCallMinutes != null && planData.planTotalCallMinutes !== "") ? String(planData.planTotalCallMinutes) : "0",
            planTotalMessages: (planData.planTotalMessages != null && planData.planTotalMessages !== "") ? String(planData.planTotalMessages) : "0",
            planProductPrice: (planData.productPrice != null && planData.productPrice !== "") ? String(planData.productPrice) : "0",
            planPsfCallMinutes: (planData.psfCallingMinutes != null && planData.psfCallingMinutes !== "") ? String(planData.psfCallingMinutes) : "0",
            planPsfMessages: (planData.psfTotalMessages != null && planData.psfTotalMessages !== "") ? String(planData.psfTotalMessages) : "0",
            planSmrCallMinutes: (planData.smrCallingMinutes != null && planData.smrCallingMinutes !== "") ? String(planData.smrCallingMinutes) : "0",
            planSmrMessages: (planData.smrTotalMessages != null && planData.smrTotalMessages !== "") ? String(planData.smrTotalMessages) : "0",
          },
        ],
      };

      if (isEditingExisting) {
        // Update existing service
        await updateService(service.serviceInfo.SERVICE_ID, payload);
        console.log(`${currentStatus === "1" ? "🟢 ACTIVE" : "🟠 INACTIVE"} Service Updated`, payload);
      } else {
        // Create new service
        console.log("📤 Sending createService request...", payload);
        await addService(payload);
        console.log("⚪ New Service Created:", payload);
      }

      await Swal.fire({
        title: "Success!",
        text: "Service published successfully.",
        icon: "success",
        showConfirmButton: false,
        timer: 1500,
        timerProgressBar: true,
      });
      if (onServiceSaved) await onServiceSaved();
      onClose();
    } catch (error) {
      console.error("❌ Error publishing service (full):", error);
      const msg =
        error?.response?.data?.creationMessage ||
        error?.response?.data?.message ||
        error?.message ||
        (typeof error === "string" ? error : error?.stack?.split("\n")?.[0]) ||
        "Failed to publish service. Please try again.";
      Swal.fire({
        title: "Error",
        text: String(msg),
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };


  const handleBackToDetails = () => {
    setShowServicePlans(false);
  };

  return (
    <main className="responsive-main">
      <h2 className="responsive-heading">
        {t("ADD_NEW_SERVICE_TITLE")} {/* Translated text */}
      </h2>

      <div className="responsive-tabs">
        <button
          className={`responsive-tab-button ${showServicePlans ? "" : "active"}`}
          type="button"
        >
          {t("SERVICE_DETAILS")} {/* Translated text */}
        </button>
        <button
          className={`responsive-tab-button ${showServicePlans ? "active" : ""}`}
          type="button"
        >
          {t("SERVICE_PLANS")} {/* Translated text */}
        </button>
      </div>

      {showServicePlans && !planOnlyMode && (
        <div style={{ marginBottom: "16px" }}>
          <button
            type="button"
            style={{
              border: "none",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "6px 12px",
              fontSize: "0.9rem",
              color: "#000",
              backgroundColor: "transparent",
              cursor: "pointer",
            }}
            onClick={handleBackToDetails}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="black"
            >
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="grey"
                strokeWidth="2"
                fill="none"
              />
              <polyline
                points="12,8 8,12 12,16"
                stroke="grey"
                strokeWidth="2"
                fill="none"
              />
            </svg>
            <span style={{ color: "grey" }}>{t("BACK_BUTTON")}</span>{" "}
            {/* Translated text */}
          </button>
        </div>
      )}

      <section className="responsive-form-container">
        {!showServicePlans ? (
          <>
            <h2
              style={{
                fontSize: "1.2rem",
                fontWeight: "bold",
                color: "#333333",
                marginBottom: "24px",
              }}
            >
              {t("SERVICE_DETAIL_INFORMATION_TITLE")} {/* Translated text */}
            </h2>

            <form className="responsive-form" onSubmit={handleSubmit}>
              <div className="responsive-form-group">
                <label htmlFor="service-name" className="responsive-label">
                  {t("SERVICE_NAME_LABEL")} {/* Translated text */}
                  <span style={{ color: "#ef4444" }}> *</span>
                </label>
                <select
                  id="service-name"
                  value={serviceName}
                  onChange={handleServiceNameChange}
                  disabled={planOnlyMode}
                  className={`responsive-input ${error.serviceName ? "error" : ""}`}
                  style={{
                    height: "48px",
                    width: "100%",
                    borderRadius: "0.25rem",
                    border: error.serviceName
                      ? "1px solid #f87171"
                      : "1px solid #d1d5db",
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                    color: serviceName ? "#374151" : "#9ca3af",
                    outline: "none",
                    backgroundColor: planOnlyMode ? "#f3f4f6" : "#ffffff",
                    cursor: planOnlyMode ? "not-allowed" : "pointer",
                  }}
                  aria-describedby="service-name-error"
                >
                  <option value={FIXED_SERVICE_NAME}>{FIXED_SERVICE_NAME}</option>
                </select>
                {error.serviceName && (
                  <p id="service-name-error" className="responsive-error">
                    <svg className="responsive-error-icon" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error.serviceName}</span>
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="service-category"
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: "normal",
                    color: "#333333",
                    marginBottom: "4px",
                  }}
                >
                  {t("SERVICE_CATEGORY_LABEL")}
                  <span style={{ color: "#ef4444" }}> *</span>
                </label>

                {/* Combo-box wrapper */}
                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    id="service-category"
                    type="text"
                    value={serviceCategory}
                    onChange={(e) => setServiceCategory(e.target.value)}
                    maxLength={50}
                    // readOnly
                    //  onClick={() => setShowCategories((prev) => !prev)}
                    placeholder={t("SELECT_CATEGORY_OPTION")}
                    style={{
                      height: "48px",
                      width: "100%",
                      borderRadius: "0.25rem",
                      border: error.serviceCategory
                        ? "1px solid #f87171"
                        : "1px solid #d1d5db",
                      padding: "8px 36px 8px 12px", // space for dropdown icon
                      fontSize: "0.875rem",
                      color: "#374151",
                      outline: "none",
                    }}
                    aria-describedby="service-category-error"
                  />

                  {/* Dropdown button (icon) */}
                  <button
                    type="button"
                    onClick={() => setShowCategories((prev) => !prev)}
                    style={{
                      position: "absolute",
                      right: "8px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "transparent",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                    }}
                  >
                    ▼
                  </button>

                  {/* Dropdown list */}
                  {showCategories && (
                    <ul
                      style={{
                        position: "absolute",
                        zIndex: 1050,
                        background: "#fff",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.25rem",
                        marginTop: "4px",
                        width: "100%",
                        maxHeight: "200px",
                        overflowY: "auto",
                        listStyle: "none",
                        padding: "0",
                      }}
                    >
                      {categories.map((cat) => (
                        <li
                          key={cat}
                          onClick={() => {
                            setServiceCategory(cat);
                            setShowCategories(false);
                          }}
                          style={{
                            padding: "8px 12px",
                            cursor: "pointer",
                            fontSize: "0.875rem",
                            color: "#374151",
                          }}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.backgroundColor = "#f3f4f6")
                          }
                          onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                          }
                        >
                          {cat}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                {error.serviceCategory && (
                  <p
                    id="service-category-error"
                    style={{
                      marginTop: "4px",
                      fontSize: "0.75rem",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <svg
                      style={{ width: "12px", height: "12px", flexShrink: 0 }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{error.serviceCategory}</span>
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="detailed-description"
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: "normal",
                    color: "#333333",
                    marginBottom: "4px",
                  }}
                >
                  {t("DETAILED_DESCRIPTION_LABEL")} {/* Translated text */}
                  <span style={{ color: "#ef4444" }}> *</span>
                </label>
                <textarea
                  id="detailed-description"
                  name="detailed-description"
                  rows="4"
                  maxLength="800"
                  placeholder={t("DETAILED_DESCRIPTION_PLACEHOLDER")} // Translated placeholder
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    resize: "none",
                    borderRadius: "0.25rem",
                    border: error.description
                      ? "1px solid #f87171"
                      : "1px solid #d1d5db",
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    outline: "none",
                  }}
                  aria-describedby="detailed-description-error"
                />
                {error.description && (
                  <p
                    id="detailed-description-error"
                    style={{
                      marginTop: "4px",
                      fontSize: "0.75rem",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <svg
                      style={{ width: "12px", height: "12px", flexShrink: 0 }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{error.description}</span>
                  </p>
                )}
                <div
                  style={{
                    textAlign: "right",
                    fontSize: "0.75rem",
                    color: "#9ca3af",
                    marginTop: "4px",
                  }}
                >
                  {description.length}/800
                </div>
              </div>

              <div>
                <label
                  htmlFor="key-features"
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: "normal",
                    color: "#333333",
                    marginBottom: "4px",
                  }}
                >
                  {t("KEY_FEATURES_LABEL")} {/* Translated text */}
                  <span style={{ color: "#ef4444" }}> *</span>
                </label>
                <textarea
                  id="key-features"
                  name="key-features"
                  rows="4"
                  maxLength="800"
                  placeholder={t("KEY_FEATURES_PLACEHOLDER")} // Translated placeholder
                  value={keyfeature}
                  onChange={(e) => setKeyfeature(e.target.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    resize: "none",
                    borderRadius: "0.25rem",
                    border: error.keyfeature
                      ? "1px solid #f87171"
                      : "1px solid #d1d5db",
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    outline: "none",
                  }}
                  aria-describedby="detailed-keyfeature-error"
                />
                {error.keyfeature && (
                  <p
                    id="detailed-keyfeature-error"
                    style={{
                      marginTop: "4px",
                      fontSize: "0.75rem",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <svg
                      style={{ width: "12px", height: "12px", flexShrink: 0 }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{error.keyfeature}</span>
                  </p>
                )}
                <div
                  style={{
                    textAlign: "right",
                    fontSize: "0.75rem",
                    color: "#9ca3af",
                    marginTop: "4px",
                  }}
                >
                  {keyfeature.length}/800
                </div>
              </div>

              <div>
                <label
                  htmlFor="top-benefits"
                  style={{
                    display: "block",
                    fontSize: "0.85rem",
                    fontWeight: "normal",
                    color: "#333333",
                    marginBottom: "4px",
                  }}
                >
                  {t("TOP_BENEFITS_LABEL")} {/* Translated text */}
                  <span style={{ color: "#ef4444" }}> *</span>
                </label>
                <textarea
                  id="top-benefits"
                  name="top-benefits"
                  rows="4"
                  maxLength="800"
                  placeholder={t("TOP_BENEFITS_PLACEHOLDER")} // Translated placeholder
                  value={topbenefits}
                  onChange={(e) => setTopbenefits(e.target.value)}
                  style={{
                    display: "block",
                    width: "100%",
                    resize: "none",
                    borderRadius: "0.25rem",
                    border: error.topbenefits
                      ? "1px solid #f87171"
                      : "1px solid #d1d5db",
                    padding: "8px 12px",
                    fontSize: "0.875rem",
                    color: "#6b7280",
                    outline: "none",
                  }}
                  aria-describedby="detailed-keyfeature-error"
                />
                {error.topbenefits && (
                  <p
                    id="detailed-keyfeature-error"
                    style={{
                      marginTop: "4px",
                      fontSize: "0.75rem",
                      color: "#ef4444",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <svg
                      style={{ width: "12px", height: "12px", flexShrink: 0 }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <span>{error.topbenefits}</span>
                  </p>
                )}
                <div
                  style={{
                    textAlign: "right",
                    fontSize: "0.75rem",
                    color: "#9ca3af",
                    marginTop: "4px",
                  }}
                >
                  {topbenefits.length}/800
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end", // aligns both buttons to right
                  marginTop: "32px",
                  height: "45px",
                }}
              >
                <div style={{ display: "flex", gap: "16px" }}>
                  {/* CANCEL BUTTON */}
                  <button
                    type="button"
                    style={{
                      border: "1px solid #171c8f",
                      color: "#171c8f",
                      borderRadius: "0.25rem",
                      padding: "8px 16px",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      backgroundColor: "transparent",
                    }}
                    onClick={handleCancel}
                  >
                    {t("CANCEL_BUTTON")}
                  </button>

                  {/* DRAFT BUTTON (commented out for now) */}
                  {/*
                  <button
                    type="button"
                    style={{
                      border: "1px solid #171c8f",
                      borderRadius: "0.25rem",
                      padding: "8px 16px",
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      color: "#171c8f",
                      backgroundColor: "transparent",
                    }}
                  >
                    {t("DRAFT_BUTTON")}
                  </button>
                  */}

                  {/* CONTINUE BUTTON */}
                  <button
                    type="submit"
                    style={{
                      borderStyle: "hidden",
                      backgroundColor: "#171c8f",
                      color: "#ffffff",
                      borderRadius: "0.25rem",
                      padding: "8px 20px",
                      fontSize: "0.875rem",
                      fontWeight: "normal",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <span>{t("CONTINUE_BUTTON")}</span>
                    <svg
                      style={{ width: "12px", height: "12px" }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

              </div>
            </form>
          </>
        ) : (
          <ServicePlanInformation
          service={serviceDetailsWithPlan ?? (serviceDetails ? { ...serviceDetails, planInfo: [] } : null)}
          category={serviceDetails?.serviceCategory || serviceDetailsWithPlan?.serviceCategory || service?.serviceInfo?.SERVICE_CATEGORY || "SMR + PSF"}
          onCancel={handleCancelPlans}
            onClose={onClose}
            onDraft={
              planOnlyMode
                ? editPlanMode
                  ? handleUpdatePlan
                  : handleAddPlanOnly
                : handleDraftService
            }
            onPublish={
              planOnlyMode
                ? editPlanMode
                  ? handleUpdatePlan
                  : handleAddPlanOnly
                : handlePublishService
            }
            planOnlyMode={planOnlyMode}
            editPlanMode={editPlanMode}
          />



        )}
      </section>
    </main>
  );
};

export default AddNewService;
